import type { SubEvaluation } from '@/teoCore/TeoApi';
import type { TeoPDF } from '@/utils/teo-pdf/TeoPDF';

import { C, percentageToColor } from './utils';

export function renderQualitativeAnalyse(
  pdf: TeoPDF,
  analysisData: Maybe<Record<string, SubEvaluation>>,
) {
  const textBoxWidth = 320;
  const origin = pdf.position;
  const filteredAnalysisData = Object.entries(analysisData ?? {})
    .filter(([, { used }]) => !!used)
    .map(([key, data]) => ({ key, ...data }));

  const graphX = origin.x + textBoxWidth + 10;
  const graphPos = {
    x: graphX,
    y: origin.y,
    // width: pageBounds.right - graphX,
  };
  if (analysisData) {
    addAnalysisGraph(pdf, {
      data: filteredAnalysisData,
      ...graphPos,
    });
  }

  pdf.moveTextTo(origin);

  pdf.addSection('Qualitative Analyse', (pdf) =>
    pdf
      .setFontSize(12)
      .addText(
        'Messen, was die Wirkung verstärkt: Verständlichkeit ist die Basis. Bildhafte Sprache verstärkt die Emotionen und bleibt besser in Erinnerung. Die persönliche Ansprache des Lesers erhöht die Relevanz und die Bindung. Konkrete Führung und Aktivierung unterstützt die gewünschte Reaktion. Diese Aspekte zahlen direkt auf die Performance ein. Ihre Gewichtung richtet sich nach der Analyse-Konfiguration. Die Bewertung erfolgt auf einer Skala von 0 bis 100%. Je höher der erreichte Wert, desto besser die prognostizierte Wirkung des Textes.',
        { align: 'justify', width: 320 },
      )
      .moveDown(1)
      .setFontSize(10)
      .setFontColor(C.Label)
      .distributedTextLine(
        filteredAnalysisData.map(({ key, title }) => `${mapKey(key)}: ${title}`),
      ),
  );

  return pdf;
}

interface CreateAnalysisGraphParams {
  data: (SubEvaluation & { key: string })[] | undefined;
  x?: number;
  y?: number;
  width?: number;
}

export function addAnalysisGraph(
  pdf: TeoPDF,
  { data, x, y, width: paramsWidth }: CreateAnalysisGraphParams,
) {
  if (!data) return;
  const origin = pdf.position;
  const offset = {
    x: x ?? origin.x,
    y: y ?? origin.y,
  };
  const width = paramsWidth ?? pdf.getPageBounds().right - offset.x;

  const maxValue = 100;

  for (const { key, score } of data) {
    const value = score || 0;
    const color = percentageToColor(value);
    const blockWidth = (value / maxValue) * (width - 30);
    const textValue = value >= 100 ? value.toFixed(0) : value.toFixed(1);
    pdf
      .setFillColor(C.Label)

      // label
      .addText(mapKey(key), {
        pos: { x: offset.x + 4, y: offset.y + 13 },
        color: C.Label,
      })
      // graph block "säule"
      .addRect({ x: offset.x + 30, y: offset.y + 8, width: blockWidth, height: 20 }, color)
      // trenner
      .addRect({ x: offset.x + 28, y: offset.y, width: 2, height: 36 }, C.Label)
      // wert innerhalb des blocks, wenn dieser lang genug ist
      .addText(textValue, {
        pos: {
          x: offset.x + 30 + blockWidth + (value < 18 ? 4 : -32),
          y: offset.y + 13.25,
        },
        color: value < 18 ? C.Text : C.TextOnDark,
      })
      .setFontColor(C.Text);

    offset.y += 36;
  }
}

export function mapKey(key: string) {
  switch (key) {
    case 'comprehensibility':
      return 'VI';
    case 'abstractness':
      return 'AI';
    case 'personal':
      return 'HI';
    case 'activation':
      return 'II';
    default:
      return key;
  }
}
